import { createContext, useContext } from 'react';

/**
 * Contract shared between {@link FilesImagePreviewGroup} and preview-enabled `FileIcon` cells.
 */
export interface FilesImagePreviewContextValue {
  getFullPreviewSrc: (fileId: number) => string | undefined;
  /** Opens the shared carousel at `fileId` when a thumbnail is registered. */
  openPreview: (fileId: number) => boolean;
  registerPreviewableImage: (fileId: number, thumbnailUrl: string) => void;
}

/** React context for full-resolution preview URLs and thumbnail registration. */
export const FilesImagePreviewContext = createContext<FilesImagePreviewContextValue | null>(null);

/**
 * Returns preview-group context when rendered inside {@link FilesImagePreviewGroup}.
 *
 * @returns Context value, or `null` outside a preview group.
 */
export function useFilesImagePreviewContext(): FilesImagePreviewContextValue | null {
  return useContext(FilesImagePreviewContext);
}

/**
 * Resolves the cached full-resolution preview URL for a file.
 *
 * @param fileId File id to look up; omit or pass `null` to skip lookup.
 * @returns Blob URL after full resolution has been fetched, otherwise `undefined`.
 */
export function useFilesImagePreviewSrc(fileId?: number | null): string | undefined {
  const context = useContext(FilesImagePreviewContext);
  if (!context || fileId == null) return undefined;
  return context.getFullPreviewSrc(fileId);
}
